import React from 'react';
import { Phone, CheckCircle2, Timer, X } from 'lucide-react';
import { ConversationWindowStatus } from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface ProviderSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProvider: (provider: 'waha' | 'official') => void;
  windowStatus: ConversationWindowStatus | null;
  wahaSession: string;
  officialPhoneId: string;
  disableOfficial?: boolean;
  disableReason?: string;
  onSendTemplate?: () => void;
}

export const ProviderSelectionModal: React.FC<ProviderSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectProvider,
  windowStatus,
  wahaSession,
  officialPhoneId,
  disableOfficial = false,
  disableReason,
  onSendTemplate
}) => {
  const { isDark } = useTheme();

  if (!isOpen) return null;

  const canSendOfficial = !disableOfficial && windowStatus?.can_send_session_message === true;
  const isExpired = !disableOfficial && windowStatus?.can_send_session_message === false && !!onSendTemplate;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`rounded-2xl shadow-xl w-full max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Escolha o número para enviar
            </h2>
            <button
              onClick={onClose}
              className={`p-1 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Você tem dois números de WhatsApp configurados. Escolha por qual deseja enviar a mensagem:
          </p>

          <div className="space-y-3">
            {/* Opção WhatsApp Business */}
            <button
              onClick={() => onSelectProvider('waha')}
              className={`w-full p-4 border-2 rounded-xl text-left transition-all group ${isDark
                  ? 'border-gray-600 hover:border-sminy-primary hover:bg-sminy-primary/10'
                  : 'border-gray-200 hover:border-sminy-primary hover:bg-sminy-primary/5'
                }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className={`font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Phone className="w-5 h-5 text-sminy-primary" />
                    WhatsApp Business
                  </div>
                  <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Sessão: <span className="font-mono text-xs">{wahaSession}</span>
                  </div>
                  <div className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Sempre disponível
                  </div>
                </div>
                <div className="text-sminy-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
              </div>
            </button>

            {/* Opção API Oficial */}
            <button
              onClick={() => {
                if (canSendOfficial) {
                  onSelectProvider('official');
                } else if (isExpired && onSendTemplate) {
                  onSendTemplate();
                }
              }}
              disabled={!canSendOfficial && !isExpired}
              className={`w-full p-4 border-2 rounded-xl text-left transition-all group ${canSendOfficial || isExpired
                  ? isDark
                    ? 'border-gray-600 hover:border-green-500 hover:bg-green-500/10 cursor-pointer'
                    : 'border-gray-200 hover:border-green-500 hover:bg-green-50 cursor-pointer'
                  : isDark
                    ? 'border-gray-700 bg-gray-700/50 cursor-not-allowed opacity-60'
                    : 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className={`font-semibold flex items-center gap-2 ${canSendOfficial
                      ? isDark ? 'text-white' : 'text-gray-900'
                      : isDark ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    WhatsApp Oficial
                  </div>
                  <div className={`text-sm mt-1 ${canSendOfficial
                      ? isDark ? 'text-gray-400' : 'text-gray-600'
                      : 'text-gray-500'
                    }`}>
                    Phone ID: <span className="font-mono text-xs">...{officialPhoneId.slice(-8)}</span>
                  </div>

                  {/* Status da Janela 24h */}
                  {windowStatus && (
                    <div className="mt-2">
                      {canSendOfficial ? (
                        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${isDark ? 'text-blue-400 bg-blue-500/20' : 'text-blue-600 bg-blue-50'
                          }`}>
                          <Timer className="w-3 h-3" />
                          <span className="font-medium">Janela ativa: {windowStatus.time_remaining}</span>
                        </div>
                      ) : (
                        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${isDark ? 'text-amber-400 bg-amber-500/20' : 'text-amber-600 bg-amber-50'
                          }`}>
                          {isExpired ? (
                            <>
                              <Timer className="w-3 h-3" />
                              <span className="font-medium">Janela expirada - Clique para enviar Template</span>
                            </>
                          ) : (
                            <span className="font-medium">⚠️ Janela de 24h expirada</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {canSendOfficial && (
                  <div className="text-green-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                )}
              </div>
            </button>
          </div>

          {/* Informação adicional */}
          {!canSendOfficial && !isExpired && (
            <div className={`mt-4 p-3 rounded-xl border ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
              }`}>
              <p className={`text-xs ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                <strong>💡 {disableOfficial ? 'Informação' : 'Dica'}:</strong>{' '}
                {disableReason || 'Para usar o WhatsApp Oficial, o cliente precisa ter enviado uma mensagem nas últimas 24 horas.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`w-full py-2.5 rounded-xl border text-sm font-medium transition-colors ${isDark
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};
